import { ITeams } from '../../../../common/store/user';
import { ICommonRequest } from '../../../../common/shared/interfaces/common-request.interface';
import { CreateReferral, PreRequest } from '../../../../common/shared/interfaces/IRequest';
import { EFileEntity, ReferralStatus } from '@shs-digi-care-monorepo/interfaces';
import { IRequestComment } from '../../../../common/shared/interfaces/IRequestComment';
import { ICreateComment } from '../../../../common/shared/interfaces/ICreateComment';
import { forkJoin, map, Observable } from 'rxjs';
import { IUploadFileInput } from '../../../../common/shared/interfaces/IUploadFileInput';

export class NamRequestOperations {
    public static findReferralIdByUserEmailAndTeams(
        request: ICommonRequest,
        userEmail: string | undefined,
        userTeams: ITeams[] | undefined
    ): string | undefined {
        const openReferrals =
            (request as PreRequest)?.referrals?.filter((ref) => ref.status === ReferralStatus.OPEN) || [];
        const referralByEmail = openReferrals.find((ref) => ref.referredOutEmail === userEmail);
        const referralByTeam = openReferrals.find((ref) => userTeams?.some((team) => team.id === ref.referredOutTeam));

        return (referralByEmail ?? referralByTeam)?.id; // Referral with matching email takes precedence
    }

    /**
     * Maps referral payload to the format required by the parallel referral API.
     * @param payload The referral payload from the action
     * @returns Mapped payload ready for API consumption
     */
    public static mapReferralPayload(
        payload: (Omit<CreateReferral, 'description'> & { comment: ICreateComment })[]
    ): Array<{
        name: string;
        referredOutEmail: string | undefined;
        referredOutTeam: string | undefined;
        isInternalReferredOut: boolean | undefined;
        description: string;
    }> {
        return payload.map((referral) => ({
            name: referral.name,
            referredOutEmail: referral.referredOutEmail,
            referredOutTeam: referral.referredOutTeam,
            isInternalReferredOut: referral.isInternalReferredOut,
            description: referral.comment?.body || ''
        }));
    }

    /**
     * Generates an array of Observables that upload attachments for each comment in the provided comments array.
     * @param comments Comments returned by /status-transition endpoint
     * @param payload Payload sent to /status-transition endpoint
     * @param attachmentUploadFn Function that uploads an attachment, returns an Observable
     * @returns Array of Observables that upload attachments for each comment
     */
    public static generateReferralCommentAttachmentUploads(
        comments: IRequestComment[],
        payload: (Omit<CreateReferral, 'description'> & {
            comment: ICreateComment;
        })[],
        attachmentUploadFn: (input: IUploadFileInput) => Observable<unknown>
    ): Observable<void>[] {
        return comments.reduce((acc, curr, currIdx) => {
            const commentId = curr.id;
            const requestId = curr.requestId;
            const attachments = payload[currIdx]?.comment.attachments || [];
            const attachmentUploads = attachments.map((att) =>
                attachmentUploadFn({
                    fileEntity: EFileEntity.Comment,
                    requestId,
                    commentId,
                    file: att
                })
            );
            if (attachmentUploads.length > 0) {
                acc.push(forkJoin(attachmentUploads).pipe(map(() => void 0)));
            }
            return acc;
        }, [] as Observable<void>[]);
    }
}
